import { DialogComponent } from '@theme/dialog';
import { unlockScroll } from '@theme/utilities';

/** localStorage: `Date.now()` when Shopify confirmed the signup. */
const STORAGE_CLAIMED = 'mystery-gift:claimed';
/** sessionStorage: the shopper closed the popup without signing up. */
const STORAGE_DISMISSED = 'mystery-gift:dismissed';
/** sessionStorage: the popup form was just submitted, waiting for the reload to confirm. */
const STORAGE_PENDING = 'mystery-gift:pending';

const DAY_IN_MS = 86_400_000;

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The popup dialog.
 * @property {HTMLElement} steps - Wrapper of the three step containers.
 * @property {HTMLButtonElement[]} box - The five gift box buttons.
 * @property {HTMLElement} form - Wrapper around the native customer form (and its state element).
 * @property {HTMLElement} formState - Hidden element carrying Shopify's post/reload result.
 * @property {HTMLElement} error - Inline error message shown at step 2.
 * @property {HTMLElement} live - Polite live region announcing step changes.
 * @property {HTMLButtonElement} closeButton - The close button.
 *
 * The theme editor button is optional and found through `data-preview`.
 *
 * @extends {DialogComponent<Refs>}
 */
class MysteryGiftComponent extends DialogComponent {
  requiredRefs = ['dialog', 'steps', 'form', 'formState', 'live'];

  /** @type {number | undefined} */
  #openTimer;

  /** @type {boolean} */
  #designMode = false;

  /** @type {number} */
  #currentStep = 1;

  /** @type {boolean} */
  #submitting = false;

  /** @type {number[]} */
  #stepTimers = [];

  /** @type {boolean} */
  #listeningOnSelf = false;

  /** @type {HTMLElement | undefined} */
  #boundForm;

  /** @type {HTMLDialogElement | undefined} */
  #boundDialog;

  connectedCallback() {
    super.connectedCallback();

    // `data-auto-open` is rendered as "false" inside the theme editor, so the modal never
    // takes over the canvas on its own while the merchant is editing.
    this.#designMode = this.dataset.autoOpen !== 'true';

    this.#bindSubtree();

    if (this.#designMode) return;

    if (this.#restoreAfterSubmit()) return;

    if (this.#isClaimed()) return;

    if (this.#isDismissed()) {
      // Closed earlier in this session: come back after the configured recall delay instead
      // of the first-visit delay.
      this.#scheduleReopen();
      return;
    }

    this.#openTimer = window.setTimeout(() => this.showDialog(), this.#delaySeconds * 1000);
  }

  /**
   * Re-renders can replace the form or the dialog, so bindings are keyed to the current
   * elements instead of being added once per connect.
   */
  #bindSubtree() {
    if (!this.#listeningOnSelf) {
      this.addEventListener('dialog:open', this.#handleOpen);
      this.addEventListener('dialog:close', this.#handleClose);
      this.addEventListener('click', this.#handleClick);
      window.addEventListener('pageshow', this.#handlePageShow);
      this.#listeningOnSelf = true;
    }

    const { form, dialog } = this.refs;

    if (form !== this.#boundForm) {
      this.#boundForm?.removeEventListener('submit', this.#handleSubmit);
      form?.addEventListener('submit', this.#handleSubmit);
      this.#boundForm = form;
    }

    if (dialog !== this.#boundDialog) {
      this.#boundDialog?.removeEventListener('close', this.#handleClose);
      dialog?.addEventListener('close', this.#handleClose);
      this.#boundDialog = dialog;
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindSubtree();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#openTimer);
    this.#clearStepTimers();

    this.removeEventListener('dialog:open', this.#handleOpen);
    this.removeEventListener('dialog:close', this.#handleClose);
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#listeningOnSelf = false;

    this.#boundForm?.removeEventListener('submit', this.#handleSubmit);
    this.#boundDialog?.removeEventListener('close', this.#handleClose);

    super.disconnectedCallback();
  }

  /**
   * A Back/Forward restore can bring back a page whose submit button was disabled while
   * the request was in flight, so the form is made usable again on every page show.
   */
  #handlePageShow = () => {
    this.#submitting = false;

    const submitButton = this.refs.form?.querySelector('button[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = false;
    }
  };

  /**
   * Moves the popup to a step, keeping the dialog label and focus in sync.
   *
   * @param {1 | 2 | 3 | 4 | 5} step
   */
  showStep(step) {
    const containers = this.refs.steps.querySelectorAll('[data-step]');
    let activeHeadingId = '';

    for (const container of containers) {
      const isActive = Number(container.dataset.step) === step;
      /** @type {HTMLElement} */ (container).hidden = !isActive;

      if (isActive) {
        activeHeadingId = container.querySelector('h2')?.id ?? '';
      }
    }

    this.#currentStep = step;

    if (activeHeadingId) {
      this.refs.dialog.setAttribute('aria-labelledby', activeHeadingId);
    }

    if (this.refs.dialog.open) {
      this.#focusStep();
    }
  }

  /**
   * Focuses the first meaningful control of the step that is now visible. The focus move is
   * deferred by a frame so it does not cut off the live-region announcement for the step.
   */
  #focusStep() {
    requestAnimationFrame(() => {
      const container = this.refs.steps.querySelector(`[data-step="${this.#currentStep}"]`);
      const target = /** @type {HTMLElement | null} */ (
        container?.querySelector('input:not([type="hidden"]), button') ?? container?.querySelector('h2')
      );

      target?.focus();
    });
  }

  #handleOpen = () => {
    this.#focusStep();
  };

  /**
   * Remember that the shopper closed the popup, so it stays quiet for the rest of the
   * session. A closed success step counts as a claim, not a dismissal.
   */
  #handleClose = () => {
    this.#clearStepTimers();
    // Safety net: if the dialog ever closes by a path that bypasses closeDialog (native
    // close event, back/forward restore), the page must not stay scroll-locked.
    if (this.refs.dialog) unlockScroll(this.refs.dialog);

    if (this.#designMode || this.#currentStep === 3) return;

    this.#write(window.sessionStorage, STORAGE_DISMISSED, '1');
    this.#scheduleReopen();
  };

  /**
   * Brings the popup back after it was closed, so a shopper who dismissed it still gets
   * another chance to claim the reward. `reopen_seconds = 0` disables the recall.
   */
  #scheduleReopen() {
    const seconds = this.#reopenSeconds;
    if (seconds <= 0 || this.#designMode) return;

    window.clearTimeout(this.#openTimer);
    this.#openTimer = window.setTimeout(() => {
      if (this.#isClaimed()) return;
      this.showDialog();
    }, seconds * 1000);
  }

  /**
   * Lets the merchant open the popup from the theme editor on demand.
   */
  #handlePreview = () => {
    this.showStep(1);
    this.showDialog();
  };

  /**
   * Picks the box the shopper tapped. Every box resolves to the same reward, so the index is
   * only used for the announcement.
   *
   * @param {number} index
   */
  #claimBox(index) {
    if (this.#currentStep !== 1) return;

    const template = this.dataset.announceTemplate ?? '';
    this.refs.live.textContent = template.replace('__INDEX__', String(index));
    this.#playOpening();
  }

  /**
   * Plays the opening beat (shake 250ms, lid lifts 450ms), then the reward, then the claim
   * form. Reduced motion skips the theatre and moves straight to the reward.
   */
  #playOpening() {
    this.#clearStepTimers();
    this.showStep(2);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      this.showStep(3);
      this.#stepTimers.push(window.setTimeout(() => this.showStep(4), 900));
      return;
    }

    this.#stepTimers.push(window.setTimeout(() => this.showStep(3), 700));
    this.#stepTimers.push(window.setTimeout(() => this.showStep(4), 2200));
  }

  #clearStepTimers() {
    for (const timer of this.#stepTimers) window.clearTimeout(timer);
    this.#stepTimers = [];
  }

  /**
   * Delegated click handler, so it keeps working when the section is re-rendered: gift box
   * buttons move the popup forward, and the theme-editor button opens it.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-preview]')) {
      this.#handlePreview();
      return;
    }

    const box = event.target.closest('[data-box-index]');
    if (box) {
      this.#claimBox(Number(box.getAttribute('data-box-index')));
    }
  };

  /**
   * Remembers that the popup form was submitted, so the reload can tell its own submission
   * apart from the footer newsletter form.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    if (this.#designMode) {
      event.preventDefault();
      return;
    }

    if (this.#submitting) {
      event.preventDefault();
      return;
    }

    this.#submitting = true;
    this.#write(window.sessionStorage, STORAGE_PENDING, '1');

    const submitButton = this.refs.form.querySelector('button[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
    }
  };

  /**
   * Applies the result Shopify rendered after the native form submission.
   *
   * @returns {boolean} Whether the popup was restored into an open step.
   */
  #restoreAfterSubmit() {
    const { formState } = this.refs;
    const hasPending = this.#read(window.sessionStorage, STORAGE_PENDING) !== null;

    if (!hasPending) return false;

    const posted = formState?.dataset.posted === 'true';
    const hasErrors = formState?.dataset.hasErrors === 'true';

    if (posted) {
      this.#markClaimed();
      this.#remove(window.sessionStorage, STORAGE_PENDING);
      this.#suppressForeignSuccessMessage();
      this.showStep(5);
      this.showDialog();
      return true;
    }

    if (hasErrors) {
      this.#remove(window.sessionStorage, STORAGE_PENDING);
      this.showStep(4);
      this.refs.error.hidden = false;
      this.showDialog();
      return true;
    }

    // A pending flag without a server answer means the submission never completed.
    this.#remove(window.sessionStorage, STORAGE_PENDING);
    return false;
  }

  /**
   * `form.posted_successfully?` is page-wide, so the theme's footer newsletter block claims
   * success after the popup's own submission. Hide that message for this page load only.
   */
  #suppressForeignSuccessMessage() {
    for (const message of document.querySelectorAll('.email-signup__message')) {
      if (message.querySelector('.icon-success')) {
        /** @type {HTMLElement} */ (message).style.display = 'none';
      }
    }
  }

  /**
   * Records a successful signup for the configured suppression window.
   */
  #markClaimed() {    const now = String(Date.now());
    this.#write(window.localStorage, STORAGE_CLAIMED, now);
    this.#write(window.sessionStorage, STORAGE_CLAIMED, now);
  }

  /** Seconds to wait before opening the popup. */
  get #delaySeconds() {
    const delay = Number(this.dataset.delay);
    return Number.isFinite(delay) && delay >= 0 ? delay : 4;
  }

  /** Seconds before the popup comes back after the shopper closed it. `0` disables it. */
  get #reopenSeconds() {
    const seconds = Number(this.dataset.reopen);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds : 600;
  }

  /** Days the popup stays hidden after a signup. `0` means "this session only". */
  get #suppressDays() {
    const days = Number(this.dataset.suppressDays);
    return Number.isFinite(days) && days >= 0 ? days : 30;
  }

  /**
   * Reads a storage entry without ever throwing. Blocked storage (Safari private mode,
   * cookie blockers) only costs the popup its memory, not its ability to work.
   *
   * @param {Storage} storage
   * @param {string} key
   * @returns {string | null}
   */
  #read(storage, key) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {Storage} storage
   * @param {string} key
   * @param {string} value
   */
  #write(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch {
      // Storage is unavailable; the popup keeps working without memory.
    }
  }

  /**
   * @param {Storage} storage
   * @param {string} key
   */
  #remove(storage, key) {
    try {
      storage.removeItem(key);
    } catch {
      // Storage is unavailable; nothing to remove.
    }
  }

  /**
   * Whether the shopper already signed up inside the configured suppression window.
   *
   * @returns {boolean}
   */
  #isClaimed() {
    const suppressDays = this.#suppressDays;

    if (suppressDays === 0) {
      return this.#read(window.sessionStorage, STORAGE_CLAIMED) !== null;
    }

    const raw = this.#read(window.localStorage, STORAGE_CLAIMED);
    if (!raw) return false;

    const claimedAt = Number(raw);
    if (!Number.isFinite(claimedAt)) return false;

    return Date.now() - claimedAt < suppressDays * DAY_IN_MS;
  }

  /**
   * Whether the shopper closed the popup during this session.
   *
   * @returns {boolean}
   */
  #isDismissed() {
    return this.#read(window.sessionStorage, STORAGE_DISMISSED) !== null;
  }
}

if (!customElements.get('mystery-gift-component')) {
  customElements.define('mystery-gift-component', MysteryGiftComponent);
}
